import { useCallback, useEffect } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { VaultProvider } from '@components/primitives';
import { Toaster } from '@vault';

import { useAuth0 } from '@hooks/useAuth0';
import RootRouter from '@routes/RootRouter';
import { initAnalytics } from '@services/analytics';
import { monitoring } from '@services/monitoring';
import { accessTokenStore } from '@state/authInfo';
import { ENV } from '@utils/env';

import queryClient from './queryClient';

import '@styles/main.css';

// Initialize analytics & monitoring
initAnalytics();
monitoring.init();

function AppWithProviders() {
  return (
    <VaultProvider>
      <Auth0Provider
        domain={ENV.auth0.domain}
        clientId={ENV.auth0.clientId}
        useRefreshTokensFallback
        authorizationParams={{
          redirect_uri: `${window.location.origin}/callback`,
          audience: ENV.auth0.audience,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </Auth0Provider>
    </VaultProvider>
  );
}

function App() {
  const { getAccessTokenSilently, logout } = useAuth0();

  const logoutHandler = useCallback(async () => {
    await logout();

    // await 2 seconds for redirect to occur, otherwise the request will fail and an
    // error boundary will be thrown.
    await new Promise((resolve) => {
      setTimeout(resolve, 2000);
    });
  }, [logout]);

  useEffect(() => {
    accessTokenStore.setGetTokenHandler(getAccessTokenSilently);
    accessTokenStore.setLogoutHandler(logoutHandler);
  }, [getAccessTokenSilently, logoutHandler]);

  return (
    <>
      <Toaster />
      <RootRouter />
      {ENV.dev &&
        window.localStorage.getItem('REACT_QUERY_DEVTOOLS') === 'true' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
    </>
  );
}

export default AppWithProviders;
